import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ChevronLeftOutlinedIcon from '@mui/icons-material/ChevronLeftOutlined';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuthContext } from '../../../context/AuthContext';
import { useNotificationContext } from '../../../context/NotificationContext';
import { FORGE_PRODUCT_RECOGNITION_CHAIN_OPTIONS as CHAIN_OPTIONS } from '../../config/chains';
import { ForgeApiService } from '../../services/ForgeApiService';
import {
  ProductRecognitionInferenceAggregatePoint,
  ProductRecognitionInferenceAggregateResponse,
  ProductRecognitionInferenceBreakdownRow,
  ProductRecognitionInferenceChainAggregateSeries,
  ProductRecognitionInferenceChainBreakdownRow,
  ProductRecognitionInferenceChainSnapshot,
  ProductRecognitionInferenceDashboardResponse,
  ProductRecognitionInferenceModPrefixAggregateSeries,
  ProductRecognitionInferencePlotAnnotation,
  ProductRecognitionInferencePlotAnnotationRequest,
  ProductRecognitionInferenceQualityStats,
  ProductRecognitionInferenceRefreshRunResponse,
  ProductRecognitionInferenceSegmentStats,
  ProductRecognitionInferenceThresholdMetric,
} from '../../types';
import { isForgeRefreshRunTimedOut, refreshRunCreatedAtMs } from '../../utils/refreshRuns';
import { safeLocalStorageGetItem, safeLocalStorageSetItem } from '../../utils/safeStorage';

type DashboardMode = 'daily' | 'aggregate';
export type PlotSegment = 'all' | 'front' | 'nonFront' | 'carton';
export type GroupMetricMode = 'pct' | 'count';
export type AxisRangeMode = 'full' | 'data';
type SimilarityThreshold = (typeof SIMILARITY_THRESHOLDS)[number];
type DeltaThreshold = (typeof DELTA_THRESHOLDS)[number];

type SegmentedControlOption<TValue extends string> = {
  value: TValue;
  label: string;
  disabled?: boolean;
};

export type TrendSeries = {
  key: string;
  label: string;
  color: string;
  values: (number | null)[];
};

export type QualityDistributionRow = {
  key: string;
  label: string;
  detectionCount: number;
  similarityPct: number | null;
  deltaPct: number | null;
};

type DailyChainNoteIdentity = {
  selectedDate: string;
  chainId: number;
};

type EditingDailyChainNote = DailyChainNoteIdentity & {
  label: string;
  valueLabel: string;
  note: string;
};

type DateRangePreset = {
  id: string;
  label: string;
  resolve: (latestCompleteDate: string) => { dateFrom: string; dateTo: string };
};

type LatestSyncStatus = 'idle' | 'checking' | 'syncing';

type LatestSyncItem = {
  chainId: number;
  chainName: string;
  selectedDate: string;
};

type LatestSyncState = {
  status: LatestSyncStatus;
  total: number;
  completed: number;
  current: LatestSyncItem | null;
};

const FULL_RANGE_DATE_FROM = '2026-05-21';
const DATE_RANGE_PRESETS: DateRangePreset[] = [
  {
    id: 'fullRange',
    label: 'Full range',
    resolve: (latestCompleteDate) => ({
      dateFrom: FULL_RANGE_DATE_FROM,
      dateTo: latestCompleteDate,
    }),
  },
  {
    id: 'last7',
    label: 'Last 7 days',
    resolve: (latestCompleteDate) => dateRangeForDays(7, latestCompleteDate),
  },
  {
    id: 'last14',
    label: 'Last 14 days',
    resolve: (latestCompleteDate) => dateRangeForDays(14, latestCompleteDate),
  },
  {
    id: 'last30',
    label: 'Last 30 days',
    resolve: (latestCompleteDate) => dateRangeForDays(30, latestCompleteDate),
  },
  {
    id: 'lastMonth',
    label: 'Last month',
    resolve: previousCalendarMonthRange,
  },
];

const controlStyle: React.CSSProperties = {
  height: 32,
  borderRadius: 7,
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text-primary)',
  padding: '0 9px',
  fontSize: 12,
};

const filterPanelStyle: React.CSSProperties = {
  padding: 10,
  display: 'grid',
  gap: 8,
  marginBottom: 12,
};

const filterGroupStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  flex: '0 0 auto',
};

const rangeSeparatorStyle: React.CSSProperties = {
  color: 'var(--text-tertiary)',
  fontSize: 13,
  fontWeight: 850,
};
const weekdayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const trendChartWidth = 780;
const trendChartHeight = 230;
const trendChartPadding = { top: 18, right: 18, bottom: 34, left: 54 };
export const SIMILARITY_THRESHOLDS = [0.75, 0.85, 0.95] as const;
export const DELTA_THRESHOLDS = [0.02, 0.05, 0.1] as const;
export const DESIGN_GROUP_SCOPES = ['full', 'partial', 'non-front'] as const;
export type DesignGroupScope = (typeof DESIGN_GROUP_SCOPES)[number];
const EMPTY_AGGREGATE_POINTS: ProductRecognitionInferenceAggregatePoint[] = [];
const COMBINED_LOCAL_TIME_VALUE = 'combined';
const LARGE_CHAIN_SNAPSHOT_HOURS = [8, 14, 20] as const;
const SNAPSHOT_HOUR_OPTIONS = [0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22];
const AXIS_RANGE_MODE_STORAGE_KEY = 'forge-pr-inference-axis-range-mode';
const LATEST_SYNC_DAY_COUNT = 3;
const LATEST_SYNC_IDLE_STATE: LatestSyncState = {
  status: 'idle',
  total: 0,
  completed: 0,
  current: null,
};

function readAxisRangeMode(): AxisRangeMode {
  return safeLocalStorageGetItem(AXIS_RANGE_MODE_STORAGE_KEY) === 'data' ? 'data' : 'full';
}

function writeAxisRangeMode(mode: AxisRangeMode): void {
  safeLocalStorageSetItem(AXIS_RANGE_MODE_STORAGE_KEY, mode);
}

const similarityQualityColor = '#2563eb';
const deltaQualityColor = '#7c3aed';
const similarityThresholdColors = ['#dc2626', '#d97706', similarityQualityColor];
const deltaThresholdColors = ['#dc2626', '#d97706', deltaQualityColor];
const chainTrendColors = [
  '#2563eb',
  '#7c3aed',
  '#059669',
  '#dc2626',
  '#d97706',
  '#0891b2',
  '#be185d',
  '#65a30d',
];
const designGroupColors: Record<string, string> = {
  full: 'var(--positive)',
  partial: 'var(--warning)',
  'non-front': 'var(--danger)',
};
const segmentControlOptions: SegmentedControlOption<PlotSegment>[] = [
  { value: 'all', label: 'All' },
  { value: 'front', label: 'Front' },
  { value: 'nonFront', label: 'Non-front' },
  { value: 'carton', label: 'Carton' },
];
const axisRangeModeOptions: SegmentedControlOption<AxisRangeMode>[] = [
  { value: 'full', label: 'Full' },
  { value: 'data', label: 'Min/max' },
];
const similarityThresholdOptions: SegmentedControlOption<string>[] = SIMILARITY_THRESHOLDS.map(
  (threshold) => ({
    value: String(threshold),
    label: formatThresholdPercent(threshold),
  })
);
const deltaThresholdOptions: SegmentedControlOption<string>[] = DELTA_THRESHOLDS.map(
  (threshold) => ({
    value: String(threshold),
    label: formatThresholdPercent(threshold),
  })
);
const modTagSegmentControlOptions: SegmentedControlOption<PlotSegment>[] = [
  { value: 'front', label: 'Front' },
  { value: 'nonFront', label: 'Non-front' },
  { value: 'carton', label: 'Carton' },
];
const designGroupScopeOptions: SegmentedControlOption<DesignGroupScope>[] = DESIGN_GROUP_SCOPES.map(
  (scope) => ({
    value: scope,
    label: formatDesignGroupScopeLabel(scope),
  })
);

const emptySegmentStats: ProductRecognitionInferenceSegmentStats = {
  total: 0,
  deltaTotal: 0,
  similarityThresholds: [],
  deltaThresholds: [],
  designGroups: [],
};

function pad(value: number): string {
  return value < 10 ? `0${value}` : `${value}`;
}

function formatSnapshotHour(hour: number): string {
  const suffix = hour < 12 ? 'AM' : 'PM';
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:00 ${suffix}`;
}

function formatSelectedLocalTime(hour?: number | null): string {
  return hour === null || hour === undefined ? 'Combined local times' : formatSnapshotHour(hour);
}

function isLargeChainSnapshotHour(hour: number): boolean {
  return (LARGE_CHAIN_SNAPSHOT_HOURS as readonly number[]).includes(hour);
}

function formatYmd(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function shiftDate(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function shiftMonth(date: Date, months: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + months, 1);
}

function parseYmd(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const [, year, month, day] = match;
  return new Date(Number(year), Number(month) - 1, Number(day));
}

function shiftYmd(value: string, days: number): string {
  const parsed = parseYmd(value);
  return parsed ? formatYmd(shiftDate(parsed, days)) : value;
}

function yesterdayYmd(): string {
  return formatYmd(shiftDate(new Date(), -1));
}

function clampYmd(value: string, max: string): string {
  if (!value) return max;
  return value > max ? max : value;
}

function clampYmdRange(value: string, min: string | undefined, max: string): string {
  if (!value) return max;
  if (min && value < min) return min;
  return value > max ? max : value;
}

function dateRangeForDays(days: number, dateTo: string): { dateFrom: string; dateTo: string } {
  return {
    dateFrom: shiftYmd(dateTo, -(days - 1)),
    dateTo,
  };
}

function previousCalendarMonthRange(latestCompleteDate: string): {
  dateFrom: string;
  dateTo: string;
} {
  const latest = parseYmd(latestCompleteDate) ?? new Date();
  const firstDayOfCurrentMonth = new Date(latest.getFullYear(), latest.getMonth(), 1);
  const lastDayOfPreviousMonth = shiftDate(firstDayOfCurrentMonth, -1);
  const firstDayOfPreviousMonth = new Date(
    lastDayOfPreviousMonth.getFullYear(),
    lastDayOfPreviousMonth.getMonth(),
    1
  );
  return {
    dateFrom: formatYmd(firstDayOfPreviousMonth),
    dateTo: formatYmd(lastDayOfPreviousMonth),
  };
}

function inclusiveDayCount(dateFrom: string, dateTo: string): number {
  const start = parseYmd(dateFrom);
  const end = parseYmd(dateTo);
  if (!start || !end) return 0;
  return Math.max(1, Math.round((end.getTime() - start.getTime()) / 86400000) + 1);
}

function monthKey(value: string): string {
  return value.slice(0, 7);
}

function formatXAxisDate(value: string, includeYear: boolean): string {
  const parsed = parseYmd(value);
  if (!parsed) return value.slice(5);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: includeYear ? undefined : 'numeric',
    year: includeYear ? '2-digit' : undefined,
  }).format(parsed);
}

function formatCalendarDate(value: string): string {
  const parsed = parseYmd(value);
  if (!parsed) return value;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(parsed);
}

function formatCalendarMonth(date: Date): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    year: 'numeric',
  }).format(date);
}

function formatPointDate(
  point: Pick<ProductRecognitionInferenceAggregatePoint, 'selectedDate' | 'isPartialDay'>
): string {
  return `${point.selectedDate}${point.isPartialDay ? '*' : ''}`;
}

export function buildXAxisTicks(
  points: ProductRecognitionInferenceAggregatePoint[]
): { index: number; label: string }[] {
  if (points.length === 0) return [];
  if (points.length === 1)
    return [{ index: 0, label: formatXAxisDate(points[0].selectedDate, false) }];
  const firstDate = points[0].selectedDate;
  const lastDate = points[points.length - 1].selectedDate;
  const dayCount = inclusiveDayCount(firstDate, lastDate);
  const tickIndexes = new Set<number>([0, points.length - 1]);

  if (dayCount <= 14) {
    points.forEach((_point, index) => tickIndexes.add(index));
  } else if (dayCount <= 62) {
    points.forEach((_point, index) => {
      if (index % 7 === 0) tickIndexes.add(index);
    });
  } else {
    points.forEach((point, index) => {
      if (index > 0 && monthKey(point.selectedDate) !== monthKey(points[index - 1].selectedDate)) {
        tickIndexes.add(index);
      }
    });
    if (dayCount > 370) {
      [...tickIndexes].forEach((index) => {
        const date = parseYmd(points[index].selectedDate);
        if (index !== 0 && index !== points.length - 1 && date && date.getMonth() % 3 !== 0) {
          tickIndexes.delete(index);
        }
      });
    }
  }

  const includeYear = dayCount > 180 || firstDate.slice(0, 4) !== lastDate.slice(0, 4);
  return [...tickIndexes]
    .sort((left, right) => left - right)
    .map((index) => ({
      index,
      label: `${formatXAxisDate(points[index].selectedDate, includeYear)}${
        points[index].isPartialDay ? '*' : ''
      }`,
    }));
}

function formatNumber(value?: number | null): string {
  if (value === undefined || value === null) return '-';
  return value.toLocaleString();
}

function formatDecimal(value?: number | null, digits = 3): string {
  if (value === undefined || value === null) return '-';
  return value.toFixed(digits);
}

function formatPercent(value?: number | null): string {
  if (value === undefined || value === null) return '-';
  return `${value.toFixed(1)}%`;
}

function formatThresholdPercent(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function formatDesignGroupScopeLabel(scope: DesignGroupScope): string {
  return scope === 'non-front' ? 'Non-front' : scope.charAt(0).toUpperCase() + scope.slice(1);
}

function chainLabel(chainId: number): string {
  return CHAIN_OPTIONS.find((chain) => chain.id === chainId)?.label || `Chain ${chainId}`;
}

function statsOrNull(snapshot?: ProductRecognitionInferenceChainSnapshot | null) {
  return snapshot?.stats ?? null;
}

function todayYmdForTimeZone(timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const part = (type: string) => parts.find((entry) => entry.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}

function getSegmentStats(
  stats: ProductRecognitionInferenceQualityStats | null | undefined,
  segment: PlotSegment
): ProductRecognitionInferenceSegmentStats {
  return stats?.segments?.[segment] ?? emptySegmentStats;
}

function metricByThreshold(
  metrics: ProductRecognitionInferenceThresholdMetric[],
  threshold: number
): ProductRecognitionInferenceThresholdMetric | null {
  return metrics.find((metric) => Math.abs(metric.threshold - threshold) < 0.0001) ?? null;
}

function pctAtOrAboveThreshold(
  metrics: ProductRecognitionInferenceThresholdMetric[],
  threshold: number
): number | null {
  const metric = metricByThreshold(metrics, threshold);
  if (!metric || metric.total <= 0) return null;
  return Math.max(0, Math.min(100, 100 - metric.pct));
}

function similarityAtLeastPct(
  stats: ProductRecognitionInferenceQualityStats | null | undefined,
  threshold: number
): number | null {
  return pctAtOrAboveThreshold(getSegmentStats(stats, 'all').similarityThresholds, threshold);
}

function deltaAtLeastPct(
  stats: ProductRecognitionInferenceQualityStats | null | undefined,
  threshold: number
): number | null {
  return pctAtOrAboveThreshold(getSegmentStats(stats, 'all').deltaThresholds, threshold);
}

export function buildSimilarityThresholdTrendSeries(
  points: ProductRecognitionInferenceAggregatePoint[],
  segmentKey: PlotSegment
): TrendSeries[] {
  return SIMILARITY_THRESHOLDS.map<TrendSeries>((threshold, index) => ({
    key: `similarity:${threshold}`,
    label: `> ${formatThresholdPercent(threshold)}`,
    color: similarityThresholdColors[index],
    values: points.map((point) => {
      const segment = getSegmentStats(point.stats, segmentKey);
      return pctAtOrAboveThreshold(segment.similarityThresholds, threshold);
    }),
  }));
}

export function buildDeltaThresholdTrendSeries(
  points: ProductRecognitionInferenceAggregatePoint[],
  segmentKey: PlotSegment
): TrendSeries[] {
  return DELTA_THRESHOLDS.map<TrendSeries>((threshold, index) => ({
    key: `delta:${threshold}`,
    label: `> ${formatThresholdPercent(threshold)}`,
    color: deltaThresholdColors[index],
    values: points.map((point) => {
      const segment = getSegmentStats(point.stats, segmentKey);
      return pctAtOrAboveThreshold(segment.deltaThresholds, threshold);
    }),
  }));
}

function buildChainMetricTrendSeries(
  chainPoints: ProductRecognitionInferenceChainAggregateSeries[],
  metric: 'similarity' | 'delta',
  segmentKey: PlotSegment,
  threshold: number
): TrendSeries[] {
  return chainPoints
    .map<TrendSeries>((entry, index) => {
      const values = entry.points.map((point) => {
        const segment = getSegmentStats(point.stats, segmentKey);
        if (metric === 'similarity')
          return pctAtOrAboveThreshold(segment.similarityThresholds, threshold);
        return pctAtOrAboveThreshold(segment.deltaThresholds, threshold);
      });
      return {
        key: `${metric}:${entry.chain.chainId}`,
        label: entry.chain.chainName || chainLabel(entry.chain.chainId),
        color: chainTrendColors[index % chainTrendColors.length],
        values,
      };
    })
    .filter((entry) => entry.values.some((value) => typeof value === 'number'));
}

export function buildChainSimilarityTrendSeries(
  chainPoints: ProductRecognitionInferenceChainAggregateSeries[],
  segmentKey: PlotSegment,
  threshold = 0.95
): TrendSeries[] {
  return buildChainMetricTrendSeries(chainPoints, 'similarity', segmentKey, threshold);
}

export function buildChainDeltaTrendSeries(
  chainPoints: ProductRecognitionInferenceChainAggregateSeries[],
  segmentKey: PlotSegment,
  threshold = 0.1
): TrendSeries[] {
  return buildChainMetricTrendSeries(chainPoints, 'delta', segmentKey, threshold);
}

function buildModPrefixMetricTrendSeries(
  modPrefixPoints: ProductRecognitionInferenceModPrefixAggregateSeries[],
  metric: 'similarity' | 'delta',
  segmentKey: PlotSegment,
  threshold: number
): TrendSeries[] {
  return modPrefixPoints
    .map<TrendSeries>((entry, index) => {
      const values = entry.points.map((point) => {
        const segment = getSegmentStats(point.stats, segmentKey);
        if (metric === 'similarity')
          return pctAtOrAboveThreshold(segment.similarityThresholds, threshold);
        return pctAtOrAboveThreshold(segment.deltaThresholds, threshold);
      });
      return {
        key: `${metric}:${entry.modPrefix}`,
        label: entry.label || entry.modPrefix,
        color: chainTrendColors[index % chainTrendColors.length],
        values,
      };
    })
    .filter((entry) => entry.values.some((value) => typeof value === 'number'));
}

export function buildModPrefixSimilarityTrendSeries(
  modPrefixPoints: ProductRecognitionInferenceModPrefixAggregateSeries[],
  segmentKey: PlotSegment,
  threshold = 0.95
): TrendSeries[] {
  return buildModPrefixMetricTrendSeries(modPrefixPoints, 'similarity', segmentKey, threshold);
}

export function buildModPrefixDeltaTrendSeries(
  modPrefixPoints: ProductRecognitionInferenceModPrefixAggregateSeries[],
  segmentKey: PlotSegment,
  threshold = 0.1
): TrendSeries[] {
  return buildModPrefixMetricTrendSeries(modPrefixPoints, 'delta', segmentKey, threshold);
}

export function buildChainDesignGroupTrendSeries(
  chainPoints: ProductRecognitionInferenceChainAggregateSeries[],
  segmentKey: PlotSegment,
  scope: DesignGroupScope,
  metricMode: GroupMetricMode
): TrendSeries[] {
  return chainPoints
    .map<TrendSeries>((entry, index) => {
      const values = entry.points.map((point) => {
        const segment = getSegmentStats(point.stats, segmentKey);
        const metric = segment.designGroups.find((designGroup) => designGroup.scope === scope);
        if (!metric || metric.total <= 0) return null;
        return metricMode === 'pct' ? metric.pct : metric.count;
      });
      return {
        key: `design-group:${scope}:${entry.chain.chainId}`,
        label: entry.chain.chainName || chainLabel(entry.chain.chainId),
        color: chainTrendColors[index % chainTrendColors.length],
        values,
      };
    })
    .filter((entry) => entry.values.some((value) => typeof value === 'number'));
}

export function buildDesignGroupTrendSeries(
  points: ProductRecognitionInferenceAggregatePoint[],
  segmentKey: PlotSegment,
  metricMode: GroupMetricMode
): TrendSeries[] {
  return DESIGN_GROUP_SCOPES.map<TrendSeries>((scope) => ({
    key: `design-group:${scope}`,
    label: formatDesignGroupScopeLabel(scope),
    color: designGroupColors[scope],
    values: points.map((point) => {
      const segment = getSegmentStats(point.stats, segmentKey);
      const metric = segment.designGroups.find((entry) => entry.scope === scope);
      if (!metric) return null;
      return metricMode === 'pct' ? metric.pct : metric.count;
    }),
  }));
}

function truncateLabel(value: string, maxLength: number): string {
  return value.length > maxLength ? `${value.slice(0, maxLength - 3)}...` : value;
}

function combinedQualityPct(row: QualityDistributionRow): number {
  return (row.similarityPct ?? 0) + (row.deltaPct ?? 0);
}

export function buildModPrefixQualityDistributionRows(
  rows: ProductRecognitionInferenceBreakdownRow[],
  similarityThreshold = 0.95,
  deltaThreshold = 0.1
): QualityDistributionRow[] {
  return rows.map((row) => {
    return {
      key: row.key,
      label: row.label,
      detectionCount: row.stats.detectionCount,
      similarityPct: similarityAtLeastPct(row.stats, similarityThreshold),
      deltaPct: deltaAtLeastPct(row.stats, deltaThreshold),
    };
  });
}

export function buildStoreQualityDistributionRows(
  rows: ProductRecognitionInferenceBreakdownRow[],
  similarityThreshold = 0.95,
  deltaThreshold = 0.1
): QualityDistributionRow[] {
  return rows
    .filter((row) => row.stats.detectionCount > 0)
    .map((row) => ({
      key: row.key,
      label: row.label,
      detectionCount: row.stats.detectionCount,
      similarityPct: similarityAtLeastPct(row.stats, similarityThreshold),
      deltaPct: deltaAtLeastPct(row.stats, deltaThreshold),
    }))
    .sort((left, right) => {
      const leftCombinedQuality = combinedQualityPct(left);
      const rightCombinedQuality = combinedQualityPct(right);
      if (leftCombinedQuality !== rightCombinedQuality)
        return leftCombinedQuality - rightCombinedQuality;
      const leftSimilarity = left.similarityPct ?? -1;
      const rightSimilarity = right.similarityPct ?? -1;
      if (leftSimilarity !== rightSimilarity) return leftSimilarity - rightSimilarity;
      const leftDelta = left.deltaPct ?? -1;
      const rightDelta = right.deltaPct ?? -1;
      if (leftDelta !== rightDelta) return leftDelta - rightDelta;
      if (left.detectionCount !== right.detectionCount)
        return right.detectionCount - left.detectionCount;
      return left.label.localeCompare(right.label);
    });
}

export function buildChainQualityDistributionRows(
  rows: Array<
    ProductRecognitionInferenceChainBreakdownRow | ProductRecognitionInferenceChainSnapshot
  >,
  similarityThreshold = 0.95,
  deltaThreshold = 0.1
): QualityDistributionRow[] {
  return rows
    .filter((row) => row.stats && row.stats.detectionCount > 0)
    .map((row) => {
      return {
        key: String(row.chain.chainId),
        label: row.chain.chainName || chainLabel(row.chain.chainId),
        detectionCount: row.stats?.detectionCount ?? 0,
        similarityPct: similarityAtLeastPct(row.stats, similarityThreshold),
        deltaPct: deltaAtLeastPct(row.stats, deltaThreshold),
      };
    });
}

export function buildThresholdTrendPath(
  values: (number | null)[],
  maxValue: number,
  minValue = 0
): string {
  const availablePoints = values
    .map((value, index) => ({ index, value }))
    .filter((entry): entry is { index: number; value: number } => typeof entry.value === 'number');
  const valueRange = maxValue - minValue;
  const innerWidth = trendChartWidth - trendChartPadding.left - trendChartPadding.right;
  const innerHeight = trendChartHeight - trendChartPadding.top - trendChartPadding.bottom;
  const xAt = (index: number) =>
    trendChartPadding.left +
    (values.length <= 1 ? innerWidth / 2 : (index / (values.length - 1)) * innerWidth);
  const yAt = (value: number) =>
    valueRange === 0
      ? trendChartPadding.top + innerHeight / 2
      : trendChartPadding.top + (1 - (value - minValue) / valueRange) * innerHeight;

  return availablePoints
    .map(({ index, value }, pathIndex) => {
      return `${pathIndex === 0 ? 'M' : 'L'}${xAt(index)},${yAt(value)}`;
    })
    .join(' ');
}

export function buildHighSimilarityTrendPath(
  points: ProductRecognitionInferenceAggregatePoint[]
): string {
  const values = points.map((point) =>
    point.stats ? similarityAtLeastPct(point.stats, 0.95) : null
  );
  const availableValues = values.filter((value): value is number => typeof value === 'number');
  return buildThresholdTrendPath(values, Math.max(...availableValues, 1));
}

export function dailyChainNoteKey({ selectedDate, chainId }: DailyChainNoteIdentity): string {
  return JSON.stringify([selectedDate, chainId]);
}

export function buildLatestSyncQueue(
  response: ProductRecognitionInferenceAggregateResponse
): LatestSyncItem[] {
  return (response.chainPoints ?? [])
    .flatMap((series) =>
      series.points
        .filter((point) => !point.snapshotAvailable || point.isPartialDay || !point.stats)
        .map((point) => ({
          chainId: series.chain.chainId,
          chainName: series.chain.chainName || chainLabel(series.chain.chainId),
          selectedDate: point.selectedDate,
        }))
    )
    .sort((left, right) => {
      if (left.selectedDate !== right.selectedDate)
        return right.selectedDate.localeCompare(left.selectedDate);
      return left.chainName.localeCompare(right.chainName);
    });
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function SegmentedControl<TValue extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  className,
}: {
  value: TValue;
  options: SegmentedControlOption<TValue>[];
  onChange: (value: TValue) => void;
  ariaLabel: string;
  className?: string;
}): JSX.Element {
  return (
    <div
      className={['mdc-stage-tabs', className].filter(Boolean).join(' ')}
      role="group"
      aria-label={ariaLabel}
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={option.value === value ? 'is-active' : ''}
          disabled={option.disabled}
          aria-pressed={option.value === value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

const DateField: React.FC<{
  label: string;
  value: string;
  max: string;
  min?: string;
  onChange: (value: string) => void;
  showLabel?: boolean;
}> = ({ label, value, max, min, onChange, showLabel = true }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [visibleMonth, setVisibleMonth] = useState(() => {
    const parsed = parseYmd(value) ?? new Date();
    return new Date(parsed.getFullYear(), parsed.getMonth(), 1);
  });
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const selectedDate = parseYmd(value);
  const minDate = min ? parseYmd(min) : null;
  const maxDate = parseYmd(max);
  const today = formatYmd(new Date());
  const canMovePreviousDay = !min || shiftYmd(value, -1) >= min;
  const canMoveNextDay = shiftYmd(value, 1) <= max;
  const previousMonth = shiftMonth(visibleMonth, -1);
  const nextMonth = shiftMonth(visibleMonth, 1);
  const canMovePreviousMonth =
    !minDate || new Date(previousMonth.getFullYear(), previousMonth.getMonth() + 1, 0) >= minDate;
  const canMoveNextMonth =
    !maxDate || new Date(nextMonth.getFullYear(), nextMonth.getMonth(), 1) <= maxDate;

  useEffect(() => {
    const parsed = parseYmd(value);
    if (parsed) setVisibleMonth(new Date(parsed.getFullYear(), parsed.getMonth(), 1));
  }, [value]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const closeOnOutside = (event: MouseEvent) => {
      if (!wrapperRef.current || wrapperRef.current.contains(event.target as Node)) return;
      setIsOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [isOpen]);

  const calendarDays = useMemo(() => {
    const monthStart = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), 1);
    const gridStart = shiftDate(monthStart, -monthStart.getDay());
    return Array.from({ length: 42 }, (_entry, index) => shiftDate(gridStart, index));
  }, [visibleMonth]);

  const updateDate = (nextValue: string) => {
    onChange(clampYmdRange(nextValue, min, max));
  };

  const moveByDays = (days: number) => {
    updateDate(shiftYmd(value, days));
  };

  return (
    <div className="pr-inference-date-field" ref={wrapperRef}>
      {showLabel ? <span className="pr-inference-filter-label">{label}</span> : null}
      <div className="pr-inference-date-control">
        <button
          type="button"
          className="pr-inference-date-step"
          aria-label={`Previous day for ${label}`}
          disabled={!canMovePreviousDay}
          onClick={() => moveByDays(-1)}
        >
          <ChevronLeftOutlinedIcon />
        </button>
        <button
          type="button"
          className="pr-inference-date-trigger"
          aria-label={label}
          aria-expanded={isOpen}
          aria-haspopup="dialog"
          onClick={() => setIsOpen((current) => !current)}
        >
          <CalendarMonthOutlinedIcon />
          <span>{formatCalendarDate(value)}</span>
        </button>
        <button
          type="button"
          className="pr-inference-date-step"
          aria-label={`Next day for ${label}`}
          disabled={!canMoveNextDay}
          onClick={() => moveByDays(1)}
        >
          <ChevronRightOutlinedIcon />
        </button>
      </div>
      {isOpen ? (
        <div
          className="pr-inference-calendar-popover"
          role="dialog"
          aria-label={`${label} calendar`}
        >
          <div className="pr-inference-calendar-header">
            <button
              type="button"
              className="pr-inference-calendar-nav"
              aria-label={`Previous month for ${label}`}
              disabled={!canMovePreviousMonth}
              onClick={() => setVisibleMonth(previousMonth)}
            >
              <ChevronLeftOutlinedIcon />
            </button>
            <div className="pr-inference-calendar-month">{formatCalendarMonth(visibleMonth)}</div>
            <button
              type="button"
              className="pr-inference-calendar-nav"
              aria-label={`Next month for ${label}`}
              disabled={!canMoveNextMonth}
              onClick={() => setVisibleMonth(nextMonth)}
            >
              <ChevronRightOutlinedIcon />
            </button>
          </div>
          <div className="pr-inference-calendar-weekdays" aria-hidden="true">
            {weekdayLabels.map((weekday) => (
              <span key={weekday}>{weekday}</span>
            ))}
          </div>
          <div className="pr-inference-calendar-grid">
            {calendarDays.map((day) => {
              const ymd = formatYmd(day);
              const isOutsideMonth = day.getMonth() !== visibleMonth.getMonth();
              const isSelected =
                Boolean(selectedDate) &&
                selectedDate?.getFullYear() === day.getFullYear() &&
                selectedDate?.getMonth() === day.getMonth() &&
                selectedDate?.getDate() === day.getDate();
              const isDisabled = (min !== undefined && ymd < min) || ymd > max;
              const className = [
                'pr-inference-calendar-day',
                isOutsideMonth ? 'is-outside-month' : '',
                isSelected ? 'is-selected' : '',
                ymd === today ? 'is-today' : '',
              ]
                .filter(Boolean)
                .join(' ');
              return (
                <button
                  key={ymd}
                  type="button"
                  className={className}
                  disabled={isDisabled}
                  aria-pressed={isSelected}
                  aria-label={`${formatCalendarDate(ymd)}${isSelected ? ', selected' : ''}`}
                  onClick={() => {
                    updateDate(ymd);
                    setIsOpen(false);
                  }}
                >
                  {day.getDate()}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            className="pr-inference-calendar-today"
            disabled={today < (min ?? today) || today > max}
            onClick={() => {
              updateDate(today);
              setIsOpen(false);
            }}
          >
            Today
          </button>
          <span className="pr-inference-calendar-hint">Use arrows to move this date.</span>
        </div>
      ) : null}
    </div>
  );
};

const TrendChart: React.FC<{
  title: string;
  subtitle: string;
  points: ProductRecognitionInferenceAggregatePoint[];
  series: TrendSeries[];
  valueMode?: GroupMetricMode;
  segmentKey?: PlotSegment | null;
  selectedChainId?: number | null;
  annotationsByKey?: Map<string, ProductRecognitionInferencePlotAnnotation>;
  onEditAnnotation?: (annotation: EditingDailyChainNote) => void;
  controls?: React.ReactNode;
  scaleToMax?: boolean;
  axisRangeMode?: AxisRangeMode;
}> = ({
  title,
  subtitle,
  points,
  series,
  valueMode = 'pct',
  segmentKey = null,
  selectedChainId = null,
  annotationsByKey,
  onEditAnnotation,
  controls,
  scaleToMax = false,
  axisRangeMode = 'full',
}) => {
  const [hiddenSeriesKeys, setHiddenSeriesKeys] = useState<Set<string>>(new Set());
  const [hoveredTooltip, setHoveredTooltip] = useState<{
    key: string;
    x: number;
    y: number;
    lines: string[];
  } | null>(null);
  useEffect(() => {
    setHiddenSeriesKeys((current) => {
      const next = new Set([...current].filter((key) => series.some((entry) => entry.key === key)));
      return next.size === current.size ? current : next;
    });
  }, [series]);
  const visibleSeries = series.filter((entry) => !hiddenSeriesKeys.has(entry.key));
  const values = visibleSeries
    .flatMap((entry) => entry.values)
    .filter((value): value is number => value !== null);
  const dataMinValue = values.length > 0 ? Math.min(...values) : 0;
  const dataMaxValue = Math.max(...values, 0);
  const maxValue =
    axisRangeMode === 'data'
      ? dataMaxValue
      : valueMode === 'pct' && !scaleToMax
        ? 100
        : dataMaxValue > 0
          ? dataMaxValue
          : 1;
  const minValue = axisRangeMode === 'data' ? dataMinValue : 0;
  const valueRange = maxValue - minValue;
  const width = trendChartWidth;
  const height = trendChartHeight;
  const padding = trendChartPadding;
  const innerWidth = width - padding.left - padding.right;
  const innerHeight = height - padding.top - padding.bottom;
  const xAxisTicks = buildXAxisTicks(points);
  const xAt = (index: number) =>
    padding.left +
    (points.length <= 1 ? innerWidth / 2 : (index / (points.length - 1)) * innerWidth);
  const yAt = (value: number) =>
    valueRange === 0
      ? padding.top + innerHeight / 2
      : padding.top + (1 - (value - minValue) / valueRange) * innerHeight;
  const formatAxis = (value: number) =>
    valueMode === 'pct' ? formatPercent(value) : formatNumber(Math.round(value));
  const tooltipLines = hoveredTooltip?.lines.map((line, index) =>
    truncateLabel(line, index === 0 ? 48 : 72)
  );
  const tooltipWidth = tooltipLines
    ? Math.min(300, Math.max(156, Math.max(...tooltipLines.map((line) => line.length)) * 6.7 + 22))
    : 0;
  const tooltipHeight = tooltipLines ? tooltipLines.length * 16 + 14 : 0;
  const tooltipX = hoveredTooltip
    ? Math.min(width - padding.right - tooltipWidth, Math.max(padding.left, hoveredTooltip.x + 12))
    : 0;
  const tooltipY = hoveredTooltip
    ? Math.max(
        padding.top,
        hoveredTooltip.y - tooltipHeight - 14 < padding.top
          ? hoveredTooltip.y + 14
          : hoveredTooltip.y - tooltipHeight - 14
      )
    : 0;

  return (
    <div className="forge-card" style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
        <div>
          <div style={{ color: 'var(--text-primary)', fontWeight: 850 }}>{title}</div>
          <div style={{ color: 'var(--text-tertiary)', fontSize: 12, marginTop: 3 }}>
            {subtitle}
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: 8,
            minWidth: 0,
          }}
        >
          {controls ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'flex-end' }}>
              {controls}
            </div>
          ) : null}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {series.map((entry) => (
              <button
                key={entry.key}
                type="button"
                className="pr-inference-legend-button"
                aria-pressed={!hiddenSeriesKeys.has(entry.key)}
                aria-label={`Toggle ${title} ${entry.label}`}
                onClick={(event) => {
                  setHiddenSeriesKeys((current) => {
                    if (event.shiftKey) {
                      return new Set(
                        series
                          .filter((seriesEntry) => seriesEntry.key !== entry.key)
                          .map((seriesEntry) => seriesEntry.key)
                      );
                    }
                    const next = new Set(current);
                    if (next.has(entry.key)) next.delete(entry.key);
                    else next.add(entry.key);
                    return next;
                  });
                  if (event.detail > 0) event.currentTarget.blur();
                }}
                onDoubleClick={(event) => {
                  setHiddenSeriesKeys(new Set());
                  event.currentTarget.blur();
                }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  border: 0,
                  padding: 0,
                  background: 'transparent',
                  color: 'var(--text-secondary)',
                  fontSize: 11,
                  fontWeight: 800,
                  opacity: hiddenSeriesKeys.has(entry.key) ? 0.38 : 1,
                  cursor: 'pointer',
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 999,
                    background: entry.color,
                    display: 'inline-block',
                  }}
                />
                {entry.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: 250 }}>
        {[0, 0.5, 1].map((ratio) => {
          const value = minValue + valueRange * ratio;
          const y = yAt(value);
          return (
            <g key={ratio}>
              <line
                x1={padding.left}
                x2={width - padding.right}
                y1={y}
                y2={y}
                stroke="var(--border)"
                strokeDasharray={ratio === 0 ? undefined : '4 4'}
              />
              <text
                x={padding.left - 8}
                y={y + 4}
                fill="var(--text-secondary)"
                fontSize="11"
                textAnchor="end"
              >
                {formatAxis(value)}
              </text>
            </g>
          );
        })}
        {visibleSeries.map((entry) => {
          const path = buildThresholdTrendPath(entry.values, maxValue, minValue);
          return path ? (
            <path
              key={entry.key}
              d={path}
              fill="none"
              stroke={entry.color}
              strokeWidth="3"
              strokeLinecap="round"
            />
          ) : null;
        })}
        {visibleSeries.flatMap((entry) =>
          entry.values.map((value, index) => {
            const point = points[index];
            if (value === null || !point) return null;
            const noteIdentity =
              selectedChainId === null
                ? null
                : {
                    chainId: selectedChainId,
                    selectedDate: point.selectedDate,
                  };
            const annotation = noteIdentity
              ? annotationsByKey?.get(dailyChainNoteKey(noteIdentity))
              : undefined;
            const valueLabel =
              valueMode === 'pct' ? formatPercent(value) : formatNumber(Math.round(value));
            const tooltipLines = [
              `${formatPointDate(point)} · ${entry.label}: ${valueLabel}`,
              ...(annotation?.note
                ? [`Note: ${annotation.note}`]
                : onEditAnnotation && noteIdentity
                  ? ['Click to add note']
                  : []),
            ];
            const label = `${title} · ${entry.label} · ${point.selectedDate}`;
            const isEditable = Boolean(onEditAnnotation && noteIdentity);
            const x = xAt(index);
            const y = yAt(value);
            const tooltip = {
              key: `${entry.key}:${point.selectedDate}`,
              x,
              y,
              lines: tooltipLines,
            };
            const editAnnotation = () => {
              if (!onEditAnnotation || !noteIdentity) return;
              onEditAnnotation({
                ...noteIdentity,
                label,
                valueLabel,
                note: annotation?.note ?? '',
              });
            };
            return (
              <g
                key={`${entry.key}:${point.selectedDate}`}
                role={isEditable ? 'button' : undefined}
                tabIndex={isEditable ? 0 : undefined}
                aria-label={
                  isEditable
                    ? `Edit note for ${title} ${entry.label} on ${formatPointDate(point)}`
                    : undefined
                }
                style={{ cursor: isEditable ? 'pointer' : 'default' }}
                pointerEvents="all"
                onMouseEnter={() => setHoveredTooltip(tooltip)}
                onMouseMove={() => setHoveredTooltip(tooltip)}
                onMouseLeave={() =>
                  setHoveredTooltip((current) => (current?.key === tooltip.key ? null : current))
                }
                onFocus={() => setHoveredTooltip(tooltip)}
                onBlur={() =>
                  setHoveredTooltip((current) => (current?.key === tooltip.key ? null : current))
                }
                onClick={isEditable ? editAnnotation : undefined}
                onKeyDown={
                  isEditable
                    ? (event) => {
                        if (event.key !== 'Enter' && event.key !== ' ') return;
                        event.preventDefault();
                        editAnnotation();
                      }
                    : undefined
                }
              >
                <title>{tooltipLines.join('\n')}</title>
                {annotation?.note ? (
                  <circle
                    cx={x}
                    cy={y}
                    r={8}
                    fill="none"
                    stroke="var(--text-primary)"
                    strokeWidth={1.5}
                  />
                ) : null}
                <circle
                  cx={x}
                  cy={y}
                  r={annotation?.note ? 5 : 3.5}
                  fill={entry.color}
                  stroke={annotation?.note ? 'var(--surface)' : undefined}
                  strokeWidth={annotation?.note ? 2 : undefined}
                />
                <circle
                  cx={x}
                  cy={y}
                  r={7}
                  fill="transparent"
                  stroke="transparent"
                  pointerEvents="all"
                />
              </g>
            );
          })
        )}
        {hoveredTooltip && tooltipLines ? (
          <g data-testid="pr-inference-plot-tooltip" pointerEvents="none">
            <rect
              x={tooltipX}
              y={tooltipY}
              width={tooltipWidth}
              height={tooltipHeight}
              rx={7}
              fill="var(--surface)"
              stroke="var(--border)"
            />
            {tooltipLines.map((line, index) => (
              <text
                key={`${hoveredTooltip.key}:${index}`}
                x={tooltipX + 11}
                y={tooltipY + 19 + index * 16}
                fill={index === 0 ? 'var(--text-primary)' : 'var(--text-secondary)'}
                fontSize="11"
                fontWeight={index === 0 ? 800 : 700}
              >
                {line}
              </text>
            ))}
          </g>
        ) : null}
        {xAxisTicks.map((tick) => (
          <text
            key={`${points[tick.index].selectedDate}:${tick.index}`}
            x={xAt(tick.index)}
            y={height - 10}
            fill="var(--text-secondary)"
            fontSize="10"
            textAnchor="middle"
          >
            {tick.label}
          </text>
        ))}
      </svg>
    </div>
  );
};

function PlotLoadingCard({ title = 'Loading plots' }: { title?: string }) {
  return (
    <div className="forge-card pr-inference-loading-card" role="status" aria-live="polite">
      <div className="pr-inference-loading-title">{title}</div>
      <div className="pr-inference-loading-subtitle">Fetching cached dashboard snapshots...</div>
      <div className="pr-inference-loading-chart">
        <div className="pr-inference-loading-grid-line" />
        <div className="pr-inference-loading-grid-line" />
        <div className="pr-inference-loading-grid-line" />
        <div className="pr-inference-loading-line pr-inference-loading-line--one" />
        <div className="pr-inference-loading-line pr-inference-loading-line--two" />
      </div>
    </div>
  );
}

const QualityDistributionChart: React.FC<{
  title: string;
  subtitle: string;
  rows: QualityDistributionRow[];
  similarityThreshold?: number;
  deltaThreshold?: number;
  labelWidth?: number;
  labelMaxLength?: number;
  maxHeight?: number;
  rowHeight?: number;
}> = ({
  title,
  subtitle,
  rows,
  similarityThreshold = 0.95,
  deltaThreshold = 0.1,
  labelWidth = 96,
  labelMaxLength = 18,
  maxHeight,
  rowHeight = 48,
}) => {
  if (rows.length === 0) return null;

  const width = trendChartWidth;
  const height = Math.max(170, rows.length * rowHeight + 70);
  const padding = { top: 18, right: 78, bottom: 34, left: labelWidth };
  const innerWidth = width - padding.left - padding.right;
  const yForRow = (index: number) => padding.top + index * rowHeight + 18;
  const widthForPct = (value: number | null) =>
    innerWidth * (Math.max(0, Math.min(value ?? 0, 100)) / 100);
  const metricRows = [
    {
      key: 'similarity',
      label: `Sim > ${formatThresholdPercent(similarityThreshold)}`,
      color: similarityQualityColor,
      field: 'similarityPct' as const,
    },
    {
      key: 'delta',
      label: `Delta > ${formatThresholdPercent(deltaThreshold)}`,
      color: deltaQualityColor,
      field: 'deltaPct' as const,
    },
  ];

  return (
    <div className="forge-card" style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
        <div>
          <div style={{ color: 'var(--text-primary)', fontWeight: 850 }}>{title}</div>
          <div style={{ color: 'var(--text-tertiary)', fontSize: 12, marginTop: 3 }}>
            {subtitle}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {metricRows.map((metric) => (
            <span
              key={metric.key}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                color: 'var(--text-secondary)',
                fontSize: 11,
                fontWeight: 800,
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 999,
                  background: metric.color,
                  display: 'inline-block',
                }}
              />
              {metric.label}
            </span>
          ))}
        </div>
      </div>
      <div style={{ maxHeight, overflowY: maxHeight ? 'auto' : undefined }}>
        <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: height + 20 }}>
          {[0, 50, 100].map((tick) => {
            const x = padding.left + (tick / 100) * innerWidth;
            return (
              <g key={tick}>
                <line
                  x1={x}
                  x2={x}
                  y1={padding.top}
                  y2={height - padding.bottom}
                  stroke="var(--border)"
                  strokeDasharray={tick === 0 ? undefined : '4 4'}
                />
                <text
                  x={x}
                  y={height - 10}
                  fill="var(--text-secondary)"
                  fontSize="10"
                  textAnchor="middle"
                >
                  {tick}%
                </text>
              </g>
            );
          })}
          {rows.map((row, index) => {
            const y = yForRow(index);
            return (
              <g key={row.key}>
                <title>
                  {row.label}
                  {'\n'}
                  {formatNumber(row.detectionCount)} detections
                </title>
                <text
                  x={padding.left - 10}
                  y={y + 8}
                  fill="var(--text-secondary)"
                  fontSize="11"
                  fontWeight="700"
                  textAnchor="end"
                >
                  {truncateLabel(row.label, labelMaxLength)}
                </text>
                <text
                  x={padding.left - 10}
                  y={y + 22}
                  fill="var(--text-tertiary)"
                  fontSize="10"
                  textAnchor="end"
                >
                  {formatNumber(row.detectionCount)}
                </text>
                {metricRows.map((metric, metricIndex) => {
                  const value = row[metric.field];
                  const barWidth = widthForPct(value);
                  const barY = y - 8 + metricIndex * 15;
                  return (
                    <g key={`${row.key}:${metric.key}`}>
                      <rect
                        x={padding.left}
                        y={barY}
                        width={barWidth}
                        height={10}
                        rx={3}
                        fill={metric.color}
                      />
                      <text
                        x={width - 8}
                        y={barY + 9}
                        fill="var(--text-secondary)"
                        fontSize="10"
                        fontWeight="700"
                        textAnchor="end"
                      >
                        {formatPercent(value)}
                      </text>
                    </g>
                  );
                })}
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
};

const ChainTable: React.FC<{
  chains: ProductRecognitionInferenceChainSnapshot[];
  similarityThreshold?: number;
  deltaThreshold?: number;
}> = ({ chains, similarityThreshold = 0.95, deltaThreshold = 0.1 }) => (
  <div className="forge-card" style={{ padding: 0, overflow: 'hidden' }}>
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 980 }}>
        <thead>
          <tr>
            {[
              'Chain',
              'Sampled images',
              'Detections',
              `Sim >${formatThresholdPercent(similarityThreshold)}`,
              `Delta >${formatThresholdPercent(deltaThreshold)}`,
              'Mean sim',
              'Mean delta',
              'Product',
              'Carton',
            ].map((header) => (
              <th
                key={header}
                style={{
                  padding: '9px 12px',
                  textAlign: 'left',
                  color: 'var(--text-tertiary)',
                  fontSize: 11,
                  borderBottom: '1px solid var(--divider)',
                }}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {chains.map((chain, index) => {
            const stats = statsOrNull(chain);
            return (
              <tr key={`${chain.chain.chainId}:${chain.chain.chainName}:${index}`}>
                <td style={cellStyle}>
                  {chain.chain.chainName || chainLabel(chain.chain.chainId)}
                </td>
                <td style={cellStyle}>{formatNumber(stats?.imageCount)}</td>
                <td style={cellStyle}>{formatNumber(stats?.detectionCount)}</td>
                <td style={{ ...cellStyle, color: similarityQualityColor, fontWeight: 800 }}>
                  {formatPercent(similarityAtLeastPct(stats, similarityThreshold))}
                </td>
                <td style={{ ...cellStyle, color: deltaQualityColor, fontWeight: 800 }}>
                  {formatPercent(deltaAtLeastPct(stats, deltaThreshold))}
                </td>
                <td style={cellStyle}>{formatDecimal(stats?.similarity.mean)}</td>
                <td style={cellStyle}>{formatDecimal(stats?.delta.mean)}</td>
                <td style={cellStyle}>{formatPercent(stats?.productDetections.pct)}</td>
                <td style={cellStyle}>{formatPercent(stats?.cartonDetections.pct)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
);

const cellStyle: React.CSSProperties = {
  padding: '9px 12px',
  borderBottom: '1px solid var(--divider)',
  color: 'var(--text-secondary)',
  fontSize: 12,
  verticalAlign: 'top',
};

export const ProductRecognitionInferenceDashboard: React.FC = () => {
  const { token } = useAuthContext();
  const { showErrorToast, showSuccessToast } = useNotificationContext();
  const [mode, setMode] = useState<DashboardMode>('aggregate');
  const [date, setDate] = useState(yesterdayYmd);
  const [dateFrom, setDateFrom] = useState(FULL_RANGE_DATE_FROM);
  const [dateTo, setDateTo] = useState(() => formatYmd(new Date()));
  const [selectedHour, setSelectedHour] = useState<number | null>(null);
  const [chainId, setChainId] = useState<number | 'all'>('all');
  const [similaritySegment, setSimilaritySegment] = useState<PlotSegment>('all');
  const [deltaSegment, setDeltaSegment] = useState<PlotSegment>('all');
  const [modTagSegment, setModTagSegment] = useState<PlotSegment>('front');
  const [designGroupSegment, setDesignGroupSegment] = useState<PlotSegment>('all');
  const [designGroupScope, setDesignGroupScope] = useState<DesignGroupScope>('full');
  const [groupMetricMode, setGroupMetricMode] = useState<GroupMetricMode>('pct');
  const [axisRangeMode, setAxisRangeMode] = useState<AxisRangeMode>(readAxisRangeMode);
  const [similarityThreshold, setSimilarityThreshold] = useState<SimilarityThreshold>(0.95);
  const [deltaThreshold, setDeltaThreshold] = useState<DeltaThreshold>(0.1);
  const [modPrefix, setModPrefix] = useState<string | null>(null);
  const [daily, setDaily] = useState<ProductRecognitionInferenceDashboardResponse | null>(null);
  const [aggregate, setAggregate] = useState<ProductRecognitionInferenceAggregateResponse | null>(
    null
  );
  const [plotAnnotations, setPlotAnnotations] = useState<
    ProductRecognitionInferencePlotAnnotation[]
  >([]);
  const [isLoadingDashboard, setIsLoadingDashboard] = useState(false);
  const [editingAnnotation, setEditingAnnotation] = useState<EditingDailyChainNote | null>(null);
  const [annotationDraft, setAnnotationDraft] = useState('');
  const [latestSync, setLatestSync] = useState<LatestSyncState>(LATEST_SYNC_IDLE_STATE);
  const [isSavingAnnotation, setIsSavingAnnotation] = useState(false);
  const loadRequestRef = useRef(0);

  const selectedChainId = chainId === 'all' ? null : chainId;
  const isLatestSyncActive = latestSync.status !== 'idle';
  const modPrefixEnabled = mode === 'aggregate' && selectedChainId !== null;
  const canEditDailyChainNotes = mode === 'aggregate' && selectedChainId !== null;

  useEffect(() => {
    if (!modPrefixEnabled) setModPrefix(null);
  }, [modPrefixEnabled]);

  const loadDashboard = useCallback(async () => {
    const requestId = loadRequestRef.current + 1;
    loadRequestRef.current = requestId;
    setIsLoadingDashboard(true);
    try {
      if (mode === 'daily') {
        const response = await ForgeApiService.fetchProductRecognitionInferenceDashboard(
          token,
          date,
          selectedHour,
          selectedChainId
        );
        if (loadRequestRef.current !== requestId) return;
        setDaily(response);
        setPlotAnnotations([]);
      } else {
        const response = await ForgeApiService.fetchProductRecognitionInferenceAggregate(
          token,
          dateFrom,
          dateTo,
          selectedHour,
          selectedChainId,
          modPrefixEnabled ? modPrefix : null
        );
        if (loadRequestRef.current !== requestId) return;
        setAggregate(response);
        if (selectedChainId === null) {
          setPlotAnnotations([]);
        } else {
          ForgeApiService.fetchProductRecognitionInferencePlotAnnotations(
            token,
            dateFrom,
            dateTo,
            selectedChainId
          )
            .then((annotationResponse) => {
              if (loadRequestRef.current !== requestId) return;
              setPlotAnnotations(annotationResponse.annotations ?? []);
            })
            .catch((error) => {
              if (loadRequestRef.current !== requestId) return;
              showErrorToast(
                error instanceof Error ? error.message : 'Error loading PR inference notes'
              );
            });
        }
      }
    } catch (error) {
      if (loadRequestRef.current !== requestId) return;
      showErrorToast(
        error instanceof Error ? error.message : 'Error loading PR inference dashboard'
      );
    } finally {
      if (loadRequestRef.current === requestId) setIsLoadingDashboard(false);
    }
  }, [
    date,
    dateFrom,
    dateTo,
    mode,
    modPrefix,
    modPrefixEnabled,
    selectedHour,
    selectedChainId,
    showErrorToast,
    token,
  ]);

  useEffect(() => {
    void loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    writeAxisRangeMode(axisRangeMode);
  }, [axisRangeMode]);

  const chainOptions = useMemo(() => {
    const ids = new Set<number>([
      ...CHAIN_OPTIONS.map((chain) => chain.id),
      ...(daily?.supportedChainIds ?? []),
      ...(daily?.chains.map((chain) => chain.chain.chainId) ?? []),
    ]);
    return [...ids].sort((left, right) => left - right);
  }, [daily]);

  const hourOptions = useMemo(() => {
    const availableHours = mode === 'daily' ? daily?.availableHours : aggregate?.availableHours;
    const hours =
      availableHours && availableHours.length > 0 ? availableHours : SNAPSHOT_HOUR_OPTIONS;
    return [...new Set(hours)].sort((left, right) => left - right);
  }, [aggregate?.availableHours, daily?.availableHours, mode]);

  useEffect(() => {
    if (selectedHour === null || hourOptions.length === 0 || hourOptions.includes(selectedHour)) {
      return;
    }
    if (selectedHour !== null) setSelectedHour(null);
  }, [hourOptions, selectedHour]);

  const chainTimeZone = useMemo(() => {
    if (selectedChainId === null) return null;
    return (
      daily?.chains.find((chain) => chain.chain.chainId === selectedChainId)?.chain.timeZone ??
      aggregate?.chain?.timeZone ??
      null
    );
  }, [aggregate?.chain?.timeZone, daily?.chains, selectedChainId]);

  const latestCompleteDate = useMemo(
    () => (chainTimeZone ? todayYmdForTimeZone(chainTimeZone) : formatYmd(new Date())),
    [chainTimeZone]
  );
  const activeRangePresetId = useMemo(() => {
    return (
      DATE_RANGE_PRESETS.find((preset) => {
        const range = preset.resolve(latestCompleteDate);
        return dateFrom === range.dateFrom && dateTo === range.dateTo;
      })?.id ?? null
    );
  }, [dateFrom, dateTo, latestCompleteDate]);

  useEffect(() => {
    setDate((current) => clampYmd(current, latestCompleteDate));
    setDateTo((current) => clampYmd(current, latestCompleteDate));
    setDateFrom((current) => clampYmd(current, latestCompleteDate));
  }, [latestCompleteDate]);

  useEffect(() => {
    if (dateFrom > dateTo) setDateFrom(dateTo);
  }, [dateFrom, dateTo]);

  const handleDailyDateChange = useCallback(
    (nextDate: string) => setDate(clampYmd(nextDate, latestCompleteDate)),
    [latestCompleteDate]
  );

  const handleDateFromChange = useCallback(
    (nextDate: string) => {
      const nextDateFrom = clampYmd(nextDate, latestCompleteDate);
      setDateFrom(nextDateFrom);
      setDateTo((currentDateTo) =>
        currentDateTo < nextDateFrom ? nextDateFrom : clampYmd(currentDateTo, latestCompleteDate)
      );
    },
    [latestCompleteDate]
  );

  const handleDateToChange = useCallback(
    (nextDate: string) => {
      const nextDateTo = clampYmd(nextDate, latestCompleteDate);
      setDateTo(nextDateTo);
      setDateFrom((currentDateFrom) =>
        currentDateFrom > nextDateTo ? nextDateTo : clampYmd(currentDateFrom, latestCompleteDate)
      );
    },
    [latestCompleteDate]
  );

  const applyDateRangePreset = useCallback(
    (presetId: string) => {
      const preset = DATE_RANGE_PRESETS.find((entry) => entry.id === presetId);
      if (!preset) return;
      const range = preset.resolve(latestCompleteDate);
      setDateFrom(range.dateFrom);
      setDateTo(range.dateTo);
    },
    [latestCompleteDate]
  );

  const annotationsByKey = useMemo(() => {
    const next = new Map<string, ProductRecognitionInferencePlotAnnotation>();
    plotAnnotations.forEach((annotation) => {
      if (annotation.chainId === null || annotation.chainId === undefined) return;
      next.set(
        dailyChainNoteKey({
          selectedDate: annotation.selectedDate,
          chainId: annotation.chainId,
        }),
        annotation
      );
    });
    return next;
  }, [plotAnnotations]);

  const startEditingAnnotation = useCallback((annotation: EditingDailyChainNote) => {
    setEditingAnnotation(annotation);
    setAnnotationDraft(annotation.note);
  }, []);

  const closeAnnotationEditor = useCallback(() => {
    setEditingAnnotation(null);
    setAnnotationDraft('');
  }, []);

  const savePlotAnnotation = async (noteOverride?: string) => {
    if (!editingAnnotation) return;
    setIsSavingAnnotation(true);
    const note = noteOverride ?? annotationDraft;
    try {
      const request: ProductRecognitionInferencePlotAnnotationRequest = {
        selectedDate: editingAnnotation.selectedDate,
        chainId: editingAnnotation.chainId,
        note,
      };
      const saved = await ForgeApiService.saveProductRecognitionInferencePlotAnnotation(
        token,
        request
      );
      const savedKey =
        saved.chainId === null || saved.chainId === undefined
          ? dailyChainNoteKey(editingAnnotation)
          : dailyChainNoteKey({
              selectedDate: saved.selectedDate,
              chainId: saved.chainId,
            });
      setPlotAnnotations((current) => {
        const next = current.filter((annotation) => {
          if (annotation.chainId === null || annotation.chainId === undefined) return true;
          return (
            dailyChainNoteKey({
              selectedDate: annotation.selectedDate,
              chainId: annotation.chainId,
            }) !== savedKey
          );
        });
        if (saved.note.trim()) next.push(saved);
        return next;
      });
      showSuccessToast(note.trim() ? 'Daily note saved' : 'Daily note cleared');
      closeAnnotationEditor();
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Error saving daily note');
    } finally {
      setIsSavingAnnotation(false);
    }
  };

  const waitForLatestSyncRun = useCallback(
    async (
      initialRun: ProductRecognitionInferenceRefreshRunResponse
    ): Promise<ProductRecognitionInferenceRefreshRunResponse> => {
      const createdAtMs = refreshRunCreatedAtMs(initialRun.createdAtUtc);
      let currentRun = initialRun;

      while (currentRun.status === 'running') {
        if (isForgeRefreshRunTimedOut(createdAtMs)) {
          throw new Error('PR inference sync timed out. Start a new latest sync to retry.');
        }
        const polledRun = await ForgeApiService.fetchProductRecognitionInferenceRefreshRun(
          token,
          currentRun.runId
        );
        currentRun = polledRun;
        if (currentRun.status === 'running') await delay(5000);
      }

      if (currentRun.status === 'failed') {
        throw new Error(currentRun.error || 'PR inference sync failed');
      }
      return currentRun;
    },
    [token]
  );

  const syncLatestSnapshots = async () => {
    if (isLatestSyncActive) return;
    const syncDateTo = formatYmd(new Date());
    const syncDateFrom = shiftYmd(syncDateTo, -(LATEST_SYNC_DAY_COUNT - 1));
    setLatestSync({
      status: 'checking',
      total: 0,
      completed: 0,
      current: null,
    });
    try {
      const response = await ForgeApiService.fetchProductRecognitionInferenceAggregate(
        token,
        syncDateFrom,
        syncDateTo,
        null,
        null,
        null
      );
      const queue = buildLatestSyncQueue(response);
      if (queue.length === 0) {
        showSuccessToast('Latest PR inference snapshots are up to date');
        setLatestSync(LATEST_SYNC_IDLE_STATE);
        return;
      }

      for (let index = 0; index < queue.length; index += 1) {
        const item = queue[index];
        setLatestSync({
          status: 'syncing',
          total: queue.length,
          completed: index,
          current: item,
        });
        const run = await ForgeApiService.createProductRecognitionInferenceRefreshRun(token, {
          chainId: item.chainId,
          date: item.selectedDate,
          similarityThreshold: 0.95,
          deltaThreshold: 0.1,
          mergeLinkedCandidates: true,
        });
        await waitForLatestSyncRun(run);
      }

      showSuccessToast(
        `Synced ${queue.length} latest PR inference snapshot${queue.length === 1 ? '' : 's'}`
      );
      setLatestSync(LATEST_SYNC_IDLE_STATE);
      void loadDashboard();
    } catch (error) {
      setLatestSync(LATEST_SYNC_IDLE_STATE);
      showErrorToast(
        error instanceof Error ? error.message : 'Error syncing latest PR inference snapshots'
      );
    }
  };

  const aggregatePoints = aggregate?.points ?? EMPTY_AGGREGATE_POINTS;
  const showAllChainRange = mode === 'aggregate' && selectedChainId === null;
  const generalSimilaritySeries = useMemo(() => {
    return buildSimilarityThresholdTrendSeries(aggregatePoints, similaritySegment);
  }, [aggregatePoints, similaritySegment]);

  const generalDeltaSeries = useMemo(() => {
    return buildDeltaThresholdTrendSeries(aggregatePoints, deltaSegment);
  }, [aggregatePoints, deltaSegment]);

  const chainSimilaritySeries = useMemo(() => {
    return buildChainSimilarityTrendSeries(
      aggregate?.chainPoints ?? [],
      similaritySegment,
      similarityThreshold
    );
  }, [aggregate?.chainPoints, similaritySegment, similarityThreshold]);

  const chainDeltaSeries = useMemo(() => {
    return buildChainDeltaTrendSeries(aggregate?.chainPoints ?? [], deltaSegment, deltaThreshold);
  }, [aggregate?.chainPoints, deltaSegment, deltaThreshold]);

  const modPrefixSimilaritySeries = useMemo(() => {
    return buildModPrefixSimilarityTrendSeries(
      aggregate?.modPrefixPoints ?? [],
      modTagSegment,
      similarityThreshold
    );
  }, [aggregate?.modPrefixPoints, modTagSegment, similarityThreshold]);

  const modPrefixDeltaSeries = useMemo(() => {
    return buildModPrefixDeltaTrendSeries(
      aggregate?.modPrefixPoints ?? [],
      modTagSegment,
      deltaThreshold
    );
  }, [aggregate?.modPrefixPoints, deltaThreshold, modTagSegment]);

  const designGroupSeries = useMemo(() => {
    if (showAllChainRange) {
      return buildChainDesignGroupTrendSeries(
        aggregate?.chainPoints ?? [],
        designGroupSegment,
        designGroupScope,
        groupMetricMode
      );
    }
    return buildDesignGroupTrendSeries(aggregatePoints, designGroupSegment, groupMetricMode);
  }, [
    aggregate?.chainPoints,
    aggregatePoints,
    designGroupScope,
    designGroupSegment,
    groupMetricMode,
    showAllChainRange,
  ]);
  const selectedDailyChain = useMemo(
    () =>
      selectedChainId === null
        ? null
        : (daily?.chains.find((chain) => chain.chain.chainId === selectedChainId) ?? null),
    [daily?.chains, selectedChainId]
  );
  const dailyModPrefixDistributionRows = useMemo(
    () =>
      buildModPrefixQualityDistributionRows(
        selectedDailyChain?.modPrefixes ?? [],
        similarityThreshold,
        deltaThreshold
      ),
    [deltaThreshold, selectedDailyChain?.modPrefixes, similarityThreshold]
  );
  const dailyStoreDistributionRows = useMemo(
    () =>
      buildStoreQualityDistributionRows(
        selectedDailyChain?.stores ?? [],
        similarityThreshold,
        deltaThreshold
      ),
    [deltaThreshold, selectedDailyChain?.stores, similarityThreshold]
  );
  const dailyChainDistributionRows = useMemo(
    () =>
      buildChainQualityDistributionRows(daily?.chains ?? [], similarityThreshold, deltaThreshold),
    [daily?.chains, deltaThreshold, similarityThreshold]
  );
  const selectedDailyMissing =
    mode === 'daily' &&
    selectedChainId !== null &&
    Boolean(daily) &&
    (!selectedDailyChain || selectedDailyChain.status === 'missing' || !selectedDailyChain.stats);
  const hasPartialDayResult =
    mode === 'daily'
      ? Boolean(daily?.chains.some((chain) => chain.isPartialDay))
      : aggregatePoints.some((point) => point.isPartialDay);
  const showDailyModPrefixDistribution =
    mode === 'daily' &&
    selectedChainId !== null &&
    !selectedDailyMissing &&
    dailyModPrefixDistributionRows.length > 0;
  const showDailyStoreDistribution =
    mode === 'daily' &&
    selectedChainId !== null &&
    !selectedDailyMissing &&
    dailyStoreDistributionRows.length > 0;
  const showDailyChainDistribution =
    mode === 'daily' && selectedChainId === null && dailyChainDistributionRows.length > 0;

  const aggregateRows = useMemo(
    () =>
      aggregatePoints.map((point, index) => ({
        chain: {
          chainId: index,
          chainName: formatPointDate(point),
          timeZone: aggregate?.chain?.timeZone ?? 'UTC',
        },
        status: point.snapshotAvailable ? 'available' : 'missing',
        snapshot: null,
        stats: point.stats,
        modPrefixes: [],
        stores: [],
      })),
    [aggregate?.chain?.timeZone, aggregatePoints]
  );
  const showAggregatePlotLoading = mode === 'aggregate' && isLoadingDashboard && aggregate === null;
  const showDailyPlotLoading = mode === 'daily' && isLoadingDashboard && daily === null;
  const showInlinePlotLoading =
    isLoadingDashboard && !showAggregatePlotLoading && !showDailyPlotLoading;
  const latestSyncRemaining = Math.max(0, latestSync.total - latestSync.completed);
  const latestSyncProgress =
    latestSync.total > 0 ? Math.min(100, (latestSync.completed / latestSync.total) * 100) : 0;

  return (
    <div className="page-content" style={{ padding: 18, width: '100%', boxSizing: 'border-box' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 14,
          marginBottom: 16,
        }}
      >
        <div>
          <h1 style={{ margin: 0, color: 'var(--text-primary)', fontSize: 22 }}>
            PR Inference OKRs
          </h1>
        </div>
      </div>

      <div className="forge-card pr-inference-filter-panel" style={filterPanelStyle}>
        <div className="pr-inference-filter-row">
          <div style={filterGroupStyle}>
            <SegmentedControl<DashboardMode>
              value={mode}
              options={[
                { value: 'daily', label: 'Daily' },
                { value: 'aggregate', label: 'Range' },
              ]}
              onChange={setMode}
              ariaLabel="Dashboard mode"
            />
          </div>
          <label style={filterGroupStyle}>
            <select
              aria-label="Chain"
              value={chainId}
              onChange={(event) => {
                const value = event.target.value;
                setChainId(value === 'all' ? 'all' : Number(value));
                setModPrefix(null);
              }}
              style={{ ...controlStyle, minWidth: 138 }}
            >
              <option value="all">All chains</option>
              {chainOptions.map((id) => (
                <option key={id} value={id}>
                  {chainLabel(id)}
                </option>
              ))}
            </select>
          </label>
          <label style={filterGroupStyle}>
            <select
              aria-label="Local time"
              value={selectedHour ?? COMBINED_LOCAL_TIME_VALUE}
              onChange={(event) =>
                setSelectedHour(
                  event.target.value === COMBINED_LOCAL_TIME_VALUE
                    ? null
                    : Number(event.target.value)
                )
              }
              style={{ ...controlStyle, minWidth: 144 }}
            >
              <option value={COMBINED_LOCAL_TIME_VALUE}>Combined</option>
              {hourOptions.map((hour) => (
                <option
                  key={hour}
                  value={hour}
                  style={{ fontWeight: isLargeChainSnapshotHour(hour) ? 850 : 400 }}
                >
                  {formatSnapshotHour(hour)}
                </option>
              ))}
            </select>
          </label>
          <div style={filterGroupStyle}>
            <span className="pr-inference-filter-label">Axes</span>
            <SegmentedControl<AxisRangeMode>
              value={axisRangeMode}
              options={axisRangeModeOptions}
              onChange={setAxisRangeMode}
              ariaLabel="Axis range"
            />
          </div>
        </div>
        <div className="pr-inference-filter-row pr-inference-filter-row--secondary">
          {mode === 'daily' ? (
            <DateField
              label="Snapshot date"
              value={date}
              max={latestCompleteDate}
              onChange={handleDailyDateChange}
            />
          ) : (
            <>
              <label style={filterGroupStyle}>
                <select
                  aria-label="Quick range"
                  value={activeRangePresetId ?? 'custom'}
                  onChange={(event) => {
                    if (event.target.value !== 'custom') applyDateRangePreset(event.target.value);
                  }}
                  style={{ ...controlStyle, minWidth: 148 }}
                >
                  <option value="custom">Custom range</option>
                  {DATE_RANGE_PRESETS.map((preset) => (
                    <option key={preset.id} value={preset.id}>
                      {preset.label}
                    </option>
                  ))}
                </select>
              </label>
              <DateField
                label="From"
                value={dateFrom}
                max={dateTo}
                onChange={handleDateFromChange}
                showLabel={false}
              />
              <span aria-hidden="true" style={rangeSeparatorStyle}>
                -
              </span>
              <DateField
                label="To"
                value={dateTo}
                min={dateFrom}
                max={latestCompleteDate}
                onChange={handleDateToChange}
                showLabel={false}
              />
            </>
          )}
          {modPrefixEnabled ? (
            <label style={filterGroupStyle}>
              <span className="pr-inference-filter-label">Mod prefix</span>
              <select
                aria-label="Mod prefix"
                value={modPrefix ?? ''}
                onChange={(event) => setModPrefix(event.target.value || null)}
                style={{ ...controlStyle, minWidth: 132 }}
              >
                <option value="">All mod prefixes</option>
                {(aggregate?.modPrefixes ?? []).map((prefix) => (
                  <option key={prefix} value={prefix}>
                    {prefix}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <span className="pr-inference-filter-spacer" aria-hidden="true" />
          <button
            type="button"
            className="pr-inference-sync-button"
            onClick={() => void syncLatestSnapshots()}
            disabled={isLatestSyncActive}
            title={`Check the last ${LATEST_SYNC_DAY_COUNT} days including today and sync missing or partial chain stats sequentially`}
          >
            <SyncOutlinedIcon />
            <span>{isLatestSyncActive ? 'Syncing...' : 'Sync latest'}</span>
          </button>
        </div>
      </div>

      {isLatestSyncActive ? (
        <div className="pr-inference-sync-bar" role="status" aria-live="polite">
          <div className="pr-inference-sync-bar-row">
            <div className="pr-inference-sync-bar-title">
              {latestSync.status === 'checking'
                ? 'Checking latest snapshots'
                : `Syncing ${latestSync.current?.chainName ?? 'chain'} · ${
                    latestSync.current?.selectedDate ?? ''
                  }`}
            </div>
            <div className="pr-inference-sync-bar-meta">
              {latestSync.status === 'checking'
                ? `Last ${LATEST_SYNC_DAY_COUNT} days`
                : `${latestSyncRemaining} left`}
            </div>
          </div>
          <div className="pr-inference-sync-bar-track" aria-hidden="true">
            <div
              className="pr-inference-sync-bar-progress"
              style={{ width: `${latestSyncProgress}%` }}
            />
          </div>
        </div>
      ) : null}

      {showInlinePlotLoading ? (
        <div className="pr-inference-inline-loading" role="status" aria-live="polite">
          Loading dashboard data...
        </div>
      ) : null}
      {hasPartialDayResult ? (
        <div style={{ color: 'var(--text-tertiary)', fontSize: 12, margin: '-4px 0 12px' }}>
          * Current-day result; recompute after the day ends.
        </div>
      ) : null}

      {mode === 'aggregate' ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 14 }}>
          {showAggregatePlotLoading ? (
            <>
              <PlotLoadingCard title="Loading similarity plot" />
              <PlotLoadingCard title="Loading delta plot" />
              <PlotLoadingCard title="Loading grouped design plot" />
            </>
          ) : showAllChainRange ? (
            <>
              <TrendChart
                title="Similarity by chain"
                subtitle={`Sim > ${formatThresholdPercent(similarityThreshold)} over the selected date range`}
                points={aggregatePoints}
                series={chainSimilaritySeries}
                segmentKey={similaritySegment}
                selectedChainId={selectedChainId}
                annotationsByKey={annotationsByKey}
                onEditAnnotation={canEditDailyChainNotes ? startEditingAnnotation : undefined}
                axisRangeMode={axisRangeMode}
                controls={
                  <>
                    <SegmentedControl<PlotSegment>
                      value={similaritySegment}
                      options={segmentControlOptions}
                      onChange={setSimilaritySegment}
                      ariaLabel="Similarity plot segment"
                    />
                    <SegmentedControl<string>
                      value={String(similarityThreshold)}
                      options={similarityThresholdOptions}
                      onChange={(value) =>
                        setSimilarityThreshold(Number(value) as SimilarityThreshold)
                      }
                      ariaLabel="Similarity threshold"
                    />
                  </>
                }
              />
              <TrendChart
                title="Delta by chain"
                subtitle={`Delta > ${formatThresholdPercent(deltaThreshold)} over the selected date range`}
                points={aggregatePoints}
                series={chainDeltaSeries}
                segmentKey={deltaSegment}
                selectedChainId={selectedChainId}
                annotationsByKey={annotationsByKey}
                onEditAnnotation={canEditDailyChainNotes ? startEditingAnnotation : undefined}
                axisRangeMode={axisRangeMode}
                controls={
                  <>
                    <SegmentedControl<PlotSegment>
                      value={deltaSegment}
                      options={segmentControlOptions}
                      onChange={setDeltaSegment}
                      ariaLabel="Delta plot segment"
                    />
                    <SegmentedControl<string>
                      value={String(deltaThreshold)}
                      options={deltaThresholdOptions}
                      onChange={(value) => setDeltaThreshold(Number(value) as DeltaThreshold)}
                      ariaLabel="Delta threshold"
                    />
                  </>
                }
              />
            </>
          ) : (
            <>
              <TrendChart
                title="Similarity"
                subtitle="Similarity thresholds over the selected date range"
                points={aggregatePoints}
                series={generalSimilaritySeries}
                segmentKey={similaritySegment}
                selectedChainId={selectedChainId}
                annotationsByKey={annotationsByKey}
                onEditAnnotation={canEditDailyChainNotes ? startEditingAnnotation : undefined}
                axisRangeMode={axisRangeMode}
                controls={
                  <SegmentedControl<PlotSegment>
                    value={similaritySegment}
                    options={segmentControlOptions}
                    onChange={setSimilaritySegment}
                    ariaLabel="Similarity plot segment"
                  />
                }
              />
              <TrendChart
                title="Delta"
                subtitle="Delta thresholds over the selected date range"
                points={aggregatePoints}
                series={generalDeltaSeries}
                segmentKey={deltaSegment}
                selectedChainId={selectedChainId}
                annotationsByKey={annotationsByKey}
                onEditAnnotation={canEditDailyChainNotes ? startEditingAnnotation : undefined}
                axisRangeMode={axisRangeMode}
                controls={
                  <SegmentedControl<PlotSegment>
                    value={deltaSegment}
                    options={segmentControlOptions}
                    onChange={setDeltaSegment}
                    ariaLabel="Delta plot segment"
                  />
                }
              />
              <TrendChart
                title="Similarity by mod tag"
                subtitle={`Sim > ${formatThresholdPercent(similarityThreshold)} by mod tag over the selected date range`}
                points={aggregatePoints}
                series={modPrefixSimilaritySeries}
                segmentKey={modTagSegment}
                selectedChainId={selectedChainId}
                annotationsByKey={annotationsByKey}
                onEditAnnotation={canEditDailyChainNotes ? startEditingAnnotation : undefined}
                axisRangeMode={axisRangeMode}
                controls={
                  <>
                    <SegmentedControl<PlotSegment>
                      value={modTagSegment}
                      options={modTagSegmentControlOptions}
                      onChange={setModTagSegment}
                      ariaLabel="Mod tag plot segment"
                    />
                    <SegmentedControl<string>
                      value={String(similarityThreshold)}
                      options={similarityThresholdOptions}
                      onChange={(value) =>
                        setSimilarityThreshold(Number(value) as SimilarityThreshold)
                      }
                      ariaLabel="Similarity threshold"
                    />
                  </>
                }
              />
              <TrendChart
                title="Delta by mod tag"
                subtitle={`Delta > ${formatThresholdPercent(deltaThreshold)} by mod tag over the selected date range`}
                points={aggregatePoints}
                series={modPrefixDeltaSeries}
                segmentKey={modTagSegment}
                selectedChainId={selectedChainId}
                annotationsByKey={annotationsByKey}
                onEditAnnotation={canEditDailyChainNotes ? startEditingAnnotation : undefined}
                axisRangeMode={axisRangeMode}
                controls={
                  <>
                    <SegmentedControl<PlotSegment>
                      value={modTagSegment}
                      options={modTagSegmentControlOptions}
                      onChange={setModTagSegment}
                      ariaLabel="Mod tag plot segment"
                    />
                    <SegmentedControl<string>
                      value={String(deltaThreshold)}
                      options={deltaThresholdOptions}
                      onChange={(value) => setDeltaThreshold(Number(value) as DeltaThreshold)}
                      ariaLabel="Delta threshold"
                    />
                  </>
                }
              />
            </>
          )}
          {!showAggregatePlotLoading ? (
            <>
              <TrendChart
                title="Grouped design exposure"
                subtitle={
                  showAllChainRange
                    ? `${formatDesignGroupScopeLabel(designGroupScope)} group exposure by chain`
                    : 'Affected detections whose winning reference belongs to each Master Label group type'
                }
                points={aggregatePoints}
                series={designGroupSeries}
                valueMode={groupMetricMode}
                segmentKey={designGroupSegment}
                selectedChainId={selectedChainId}
                annotationsByKey={annotationsByKey}
                onEditAnnotation={canEditDailyChainNotes ? startEditingAnnotation : undefined}
                scaleToMax
                axisRangeMode={axisRangeMode}
                controls={
                  <>
                    <SegmentedControl<PlotSegment>
                      value={designGroupSegment}
                      options={segmentControlOptions}
                      onChange={setDesignGroupSegment}
                      ariaLabel="Grouped design plot segment"
                    />
                    {showAllChainRange ? (
                      <SegmentedControl<DesignGroupScope>
                        value={designGroupScope}
                        options={designGroupScopeOptions}
                        onChange={setDesignGroupScope}
                        ariaLabel="Grouped design scope"
                      />
                    ) : null}
                    <SegmentedControl<GroupMetricMode>
                      value={groupMetricMode}
                      options={[
                        { value: 'pct', label: '%' },
                        { value: 'count', label: '#' },
                      ]}
                      onChange={setGroupMetricMode}
                      ariaLabel="Grouped design metric"
                    />
                  </>
                }
              />
              <ChainTable
                chains={aggregateRows}
                similarityThreshold={similarityThreshold}
                deltaThreshold={deltaThreshold}
              />
            </>
          ) : null}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 14 }}>
          {showDailyPlotLoading ? (
            <PlotLoadingCard title="Loading daily distribution" />
          ) : selectedDailyMissing ? (
            <div className="forge-card" style={{ padding: 16, color: 'var(--text-secondary)' }}>
              We do not have this result yet.
            </div>
          ) : showDailyChainDistribution ? (
            <QualityDistributionChart
              title="Chain quality distribution"
              subtitle={`${daily?.selectedDate ?? date}${
                daily?.chains.some((chain) => chain.isPartialDay) ? '*' : ''
              } · ${formatSelectedLocalTime(daily?.selectedHour ?? selectedHour)}`}
              rows={dailyChainDistributionRows}
              similarityThreshold={similarityThreshold}
              deltaThreshold={deltaThreshold}
            />
          ) : null}
          {showDailyModPrefixDistribution ? (
            <QualityDistributionChart
              title="Mod prefix quality distribution"
              subtitle={`${selectedDailyChain?.chain.chainName || chainLabel(selectedChainId ?? 0)} on ${
                daily?.selectedDate ?? date
              }${selectedDailyChain?.isPartialDay ? '*' : ''}`}
              rows={dailyModPrefixDistributionRows}
              similarityThreshold={similarityThreshold}
              deltaThreshold={deltaThreshold}
            />
          ) : null}
          {showDailyStoreDistribution ? (
            <QualityDistributionChart
              title="Store quality distribution"
              subtitle="Stores with detections, sorted by combined Sim/Delta quality"
              rows={dailyStoreDistributionRows}
              similarityThreshold={similarityThreshold}
              deltaThreshold={deltaThreshold}
              labelWidth={260}
              labelMaxLength={34}
              maxHeight={620}
              rowHeight={38}
            />
          ) : null}
          {!selectedDailyMissing ? (
            <ChainTable
              chains={daily?.chains ?? []}
              similarityThreshold={similarityThreshold}
              deltaThreshold={deltaThreshold}
            />
          ) : null}
        </div>
      )}
      {editingAnnotation ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Daily note editor"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            display: 'grid',
            placeItems: 'center',
            padding: 20,
            background: 'rgba(0, 0, 0, 0.45)',
          }}
        >
          <div className="forge-card" style={{ width: 'min(520px, 100%)', padding: 16 }}>
            <div style={{ color: 'var(--text-primary)', fontWeight: 850 }}>Daily note</div>
            <div style={{ marginTop: 6, color: 'var(--text-tertiary)', fontSize: 12 }}>
              {editingAnnotation.label} · {editingAnnotation.valueLabel}
            </div>
            <textarea
              aria-label="Daily note"
              value={annotationDraft}
              onChange={(event) => setAnnotationDraft(event.target.value)}
              rows={5}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                marginTop: 12,
                resize: 'vertical',
                borderRadius: 7,
                border: '1px solid var(--border)',
                background: 'var(--surface)',
                color: 'var(--text-primary)',
                padding: 10,
                fontSize: 13,
              }}
            />
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 8,
                marginTop: 12,
              }}
            >
              <button type="button" onClick={closeAnnotationEditor} style={controlStyle}>
                Cancel
              </button>
              <button
                type="button"
                disabled={isSavingAnnotation}
                onClick={() => {
                  void savePlotAnnotation('');
                }}
                style={controlStyle}
              >
                Clear
              </button>
              <button
                type="button"
                disabled={isSavingAnnotation}
                onClick={() => void savePlotAnnotation()}
                style={{
                  ...controlStyle,
                  color: 'var(--text-primary)',
                  fontWeight: 850,
                }}
              >
                {isSavingAnnotation ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};
